/// <amd-module name="Wit/WorkItemForm/Links/RemoteWorkItem/RemoteWorkItemUtils" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { LinkTypeFieldIds, RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IRemoteWorkItemAndHostData, IRemoteWorkItemAndTypeData } from "./RemoteWorkItemService";

/**
 * Converts data for an existing remote work item link (represented by the
 * linkIdentifier) to link display data.
 */
export async function toLinkDisplayData(
    pageContext: IVssPageContext,
    workItemData: IRemoteWorkItemAndTypeData,
    linkIdentifier: ILinkIdentifier | undefined
): Promise<IResolvedLinkDisplayData | undefined>;
/**
 * Creates link display data for a new link to a remote work item, given data
 * for the work item, remote host, and link type.
 */
export async function toLinkDisplayData(
    pageContext: IVssPageContext,
    workItemData: IRemoteWorkItemAndTypeData,
    hostData: IRemoteWorkItemAndHostData,
    linkTypeEnd: IWorkItemLinkTypeEnd
): Promise<IResolvedLinkDisplayData | undefined>;
export async function toLinkDisplayData(
    pageContext: IVssPageContext,
    workItemData: IRemoteWorkItemAndTypeData,
    linkIdentifierOrHostData?: ILinkIdentifier | IRemoteWorkItemAndHostData,
    linkTypeEnd?: IWorkItemLinkTypeEnd
): Promise<IResolvedLinkDisplayData | undefined> {
    const workItem = workItemData["work-item-data"];
    const workItemTypeColorAndIcon = workItemData["work-item-type-color-icon"];
    const workItemStateAndColor = workItemData["work-item-type-state-color"];

    if (!workItem || !linkIdentifierOrHostData) {
        return undefined;
    }
    const linkIdentifier = isHostData(linkIdentifierOrHostData)
        ? createNewLinkIdentifier(linkIdentifierOrHostData, linkTypeEnd!)
        : linkIdentifierOrHostData;

    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    const linksService = pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
    linkTypeEnd = linkTypeEnd ?? linkIdentifier.LinkType ? await linksService.getWitLinkTypeEnd(linkIdentifier.LinkType) : undefined;

    const id = "" + workItem.id;
    const title = "" + workItem.fields[CoreFieldRefNames.Title];
    const state = "" + workItem.fields[CoreFieldRefNames.State];
    const project = "" + workItem.fields[CoreFieldRefNames.TeamProject];
    const updateDate = workItem.fields[CoreFieldRefNames.ChangedDate] as Date;
    const assugnedTo = workItem.fields[CoreFieldRefNames.AssignedTo];

    return {
        linkIdentifier,
        id,
        displayId: id,
        title,
        state,
        updateDate,
        tool: ToolIds.RemoteWorkItemTracking,
        linkTypeDisplayName: linkTypeEnd?.name ?? RegisteredLinkTypeNames.RemoteWorkItemLink,
        registeredLinkType: RegisteredLinkTypeNames.RemoteWorkItemLink,
        witLinkTypeEnd: linkTypeEnd?.immutableName,
        uri: locationService.routeUrl("ms.vss-work-web.new-work-item-form-route", { project, id }, linkIdentifier.RemoteHostUrl),
        icons: [workItemTypeColorAndIcon, { displayName: assugnedTo }],
        group: {
            displayName: `${linkIdentifier.RemoteHostName}/${project}`,
            id: `${linkIdentifier.RemoteHostId}/${linkIdentifier.RemoteProjectId}`,
            iconName: "CloudUpload"
        },
        additionalData: {
            workItem,
            workItemTypeColorAndIcon,
            workItemStateAndColor
        }
    };
}

function isHostData(linkIdentifierOrHostData: ILinkIdentifier | IRemoteWorkItemAndHostData): linkIdentifierOrHostData is IRemoteWorkItemAndHostData {
    return "work-item-data" in linkIdentifierOrHostData;
}

function createNewLinkIdentifier(hostData: IRemoteWorkItemAndHostData, linkTypeEnd: IWorkItemLinkTypeEnd): ILinkIdentifier {
    const workItem = hostData["work-item-data"];
    return {
        tool: ToolIds.RemoteWorkItemTracking,
        ID: workItem.id,
        FldID: LinkTypeFieldIds.WorkItem,
        LinkType: linkTypeEnd.id,
        RemoteHostId: hostData["work-item-host-id"],
        RemoteHostUrl: hostData["work-item-host-url"],
        RemoteHostName: hostData["work-item-host-name"],
        RemoteProjectId: hostData["work-item-project-id"]
    };
}
